import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Mail, Phone, User, ArrowRight } from "lucide-react";
import { ASSETS, REALTOR } from "../lib/brand";
import { ANIMATION, ICON_SIZE, STORAGE_KEYS } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
    { key: "name", label: "Full name", placeholder: "Sarah Johnson", type: "text", icon: User, autoComplete: "name", testId: "lead-name-input" },
    { key: "email", label: "Email", placeholder: "sarah@example.com", type: "email", icon: Mail, autoComplete: "email", testId: "lead-email-input" },
    { key: "phone", label: "Phone", placeholder: "(850) 555-0123", type: "tel", icon: Phone, autoComplete: "tel", testId: "lead-phone-input" },
];

function parseSubmissionError(err) {
    const detail = err?.response?.data?.detail;
    // FastAPI 422 returns an array of {loc, msg, type}. Surface the first
    // field-specific message so the user knows exactly what to fix.
    if (Array.isArray(detail) && detail.length > 0) {
        const first = detail[0];
        const field = Array.isArray(first?.loc) ? first.loc[first.loc.length - 1] : null;
        const friendly = {
            name: "Please enter your full name.",
            email: "That email doesn't look right — please double-check it.",
            phone: "Please enter a valid phone number (at least 7 digits).",
        }[field];
        if (friendly) return friendly;
        if (typeof first?.msg === "string") return first.msg;
    }
    if (typeof detail === "string") return detail;
    return "We couldn't open the guide. Please check your details and try again.";
}

function persistLead(form) {
    try {
        localStorage.setItem(STORAGE_KEYS.LEAD, JSON.stringify({ ...form, ts: Date.now() }));
    } catch (error) {
        console.error("Could not persist lead", error);
    }
}

function trimForm(form) {
    return {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
    };
}

export default function LeadGate({ onUnlock }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "" });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const updateField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        const cleaned = trimForm(form);
        if (!cleaned.name || !cleaned.email || !cleaned.phone) {
            setError("Please complete all three fields.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/leads`, cleaned);
            persistLead(cleaned);
            onUnlock(cleaned);
        } catch (err) {
            console.error("Lead submission failed", err);
            setError(parseSubmissionError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            <motion.div
                key="lead-gate"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="fixed inset-0 z-50 flex items-stretch bg-cream"
                data-testid="lead-gate"
            >
                <CoverHero />
                <FormSide
                    fields={FIELDS}
                    form={form}
                    error={error}
                    submitting={submitting}
                    onChangeField={updateField}
                    onSubmit={handleSubmit}
                />
            </motion.div>
        </AnimatePresence>
    );
}

function CoverHero() {
    return (
        <div
            className="hidden md:block md:w-1/2 lg:w-3/5 relative bg-navy"
            style={{
                backgroundImage: `url(${ASSETS.coverHero})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
            }}
        >
            <div className="absolute inset-0 bg-gradient-to-tr from-navy/85 via-navy/50 to-transparent" />
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.7 }}
                className="relative z-10 h-full flex flex-col justify-between p-12 lg:p-16 text-cream"
            >
                <div>
                    <div className="eyebrow text-coral">2026 Edition · Confidential</div>
                    <div className="mt-2 text-cream/70 font-sans text-xs tracking-[0.3em] uppercase">
                        Emerald Coast Relocation Intelligence Report
                    </div>
                </div>
                <div>
                    <h1 className="serif-display text-6xl lg:text-7xl xl:text-8xl text-cream">
                        Moving<br />to Florida.
                    </h1>
                    <div className="mt-8 h-px w-16 bg-gold" />
                    <p className="mt-6 max-w-md font-sans text-cream/85 text-base leading-relaxed">
                        A hyperlocal comparison of six premier Emerald Coast communities — engineered to help you make a confident, data-backed move.
                    </p>
                </div>
                <div className="font-sans text-xs tracking-[0.2em] uppercase text-cream/60">
                    Prepared by {REALTOR.name} · REALTOR® + Loan Originator
                </div>
            </motion.div>
        </div>
    );
}

function FormSide({ fields, form, error, submitting, onChangeField, onSubmit }) {
    return (
        <div className="w-full md:w-1/2 lg:w-2/5 flex items-center justify-center p-8 sm:p-12 grain-bg overflow-y-auto">
            <motion.div
                initial={{ opacity: 0, y: ANIMATION.LEAD_GATE_FADE_OFFSET_PX }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="relative z-10 w-full max-w-md"
            >
                <div className="eyebrow">Unlock the Guide</div>
                <h2 className="serif-display text-4xl sm:text-5xl text-navy mt-3 mb-4">
                    Your private<br />Emerald Coast<br />briefing.
                </h2>
                <p className="text-muted2 leading-relaxed mb-8">
                    Six cities. Real prices. Real schools. Real insider tips. Tell me where to send your access — I'll personally follow up to answer any relocation questions.
                </p>

                <form onSubmit={onSubmit} className="space-y-4" data-testid="lead-form">
                    {fields.map((f) => {
                        const Icon = f.icon;
                        return (
                            <Field
                                key={f.key}
                                icon={<Icon size={ICON_SIZE.INLINE_LG} />}
                                label={f.label}
                                value={form[f.key]}
                                onChange={(v) => onChangeField(f.key, v)}
                                placeholder={f.placeholder}
                                type={f.type}
                                autoComplete={f.autoComplete}
                                testId={f.testId}
                            />
                        );
                    })}

                    {error && (
                        <div className="text-sm text-coral" data-testid="lead-error">
                            {error}
                        </div>
                    )}

                    <SubmitButton submitting={submitting} />

                    <p className="text-xs text-muted2 text-center pt-2">
                        Your information stays confidential and is shared only with {REALTOR.name}.
                    </p>
                </form>
            </motion.div>
        </div>
    );
}

function SubmitButton({ submitting }) {
    let inner;
    if (submitting) {
        inner = (
            <>
                <Loader2 size={ICON_SIZE.INLINE_LG} className="animate-spin" />
                Sending
            </>
        );
    } else {
        inner = (
            <>
                Open the Guide
                <ArrowRight
                    size={ICON_SIZE.INLINE_LG}
                    className="transition-transform group-hover:translate-x-1"
                />
            </>
        );
    }
    return (
        <button
            type="submit"
            disabled={submitting}
            data-testid="lead-submit-btn"
            className="group mt-2 inline-flex w-full items-center justify-center gap-2 bg-navy text-cream py-4 px-6 rounded-sm font-sans text-sm font-semibold tracking-[0.18em] uppercase transition-all duration-300 hover:bg-navy-hover hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
            {inner}
        </button>
    );
}

function Field({ icon, label, value, onChange, placeholder, type, autoComplete, testId }) {
    return (
        <label className="block">
            <span className="eyebrow text-muted2 mb-2 block" style={{ color: "#5C6B73" }}>
                {label}
            </span>
            <div className="flex items-center gap-3 border border-hairline bg-white px-4 py-3 focus-within:border-navy transition-colors">
                <span className="text-muted2">{icon}</span>
                <input
                    type={type}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    data-testid={testId}
                    autoComplete={autoComplete}
                    className="w-full bg-transparent outline-none text-ink placeholder:text-muted2/50 font-sans"
                />
            </div>
        </label>
    );
}
